import { AHWidgetTitle, Box, TouchableBox, useBoxStyle } from "arkh";
import { useSetWidgetFocus } from "../useSetFocus";
import { BaseWidgetProps } from "../BaseProps";
import {
  graphql,
  useFragment,
  useLazyLoadQuery,
  usePaginationFragment,
  useSubscribeToInvalidationState,
} from "react-relay";
import { WidgetCalendarOverview_widget$key } from "./__generated__/WidgetCalendarOverview_widget.graphql";
import { ActivityIndicator, FlatList, Platform } from "react-native";
import { useCallback, useMemo, useState } from "react";
import { WidgetCalendarOverviewQuery } from "./__generated__/WidgetCalendarOverviewQuery.graphql";
import { WidgetCalendarOverviewContent_widget$key } from "./__generated__/WidgetCalendarOverviewContent_widget.graphql";
import { WidgetCalendarOverviewEventItem } from "./WidgetCalendarOverviewEventItem";
import { WidgetCalendarOverviewWeekItem } from "./WidgetCalendarOverviewWeekItem";
import { WidgetCalendarOverviewPreview_widget$key } from "./__generated__/WidgetCalendarOverviewPreview_widget.graphql";
import { createCalendarOverviewListData } from "./createCalendarOverviewListData";
import { WidgetAddItemButton } from "../WidgetTodoOverview/WidgetAddItemButton";
import { useSubscriptionWithSkip } from "../utils/useSubscriptionWithSkip";
import { WidgetCalendarOverviewEventDeletedSubscription } from "./__generated__/WidgetCalendarOverviewEventDeletedSubscription.graphql";
import { WidgetCalendarOverviewEventCreatedSubscription } from "./__generated__/WidgetCalendarOverviewEventCreatedSubscription.graphql";
import { WidgetCalendarOverviewEventUpdatedSubscription } from "./__generated__/WidgetCalendarOverviewEventUpdatedSubscription.graphql";

function WidgetCalendarOverviewContent(props: {
  widget: WidgetCalendarOverviewContent_widget$key;
  scrollEnabled: boolean;
  skipSubscriptions?: boolean;
  onPressEvent?: (eventId: string, connections: string[]) => void;
  onPressAdd?: (connections: string[]) => void;
}) {
  const { data, hasNext, isLoadingNext, loadNext } = usePaginationFragment(
    graphql`
      fragment WidgetCalendarOverviewContent_widget on WidgetCalendarOverview
      @refetchable(queryName: "WidgetCalendarOverviewContentPaginatedQuery") {
        connectedCalendars: calendars(includeExternal: true) {
          id
        }
        calendarEvents(first: $first, after: $after)
          @connection(key: "WidgetCalendarOverviewContent__calendarEvents") {
          __id
          edges {
            node {
              id
              startAt {
                isoString
              }
              endAt {
                isoString
              }
              createdAt {
                isoString
              }
              isAllDayEvent
              ...WidgetCalendarOverviewEventItem_event
            }
          }
        }
      }
    `,
    props.widget
  );

  const connection = data.calendarEvents?.__id;

  useSubscriptionWithSkip<WidgetCalendarOverviewEventCreatedSubscription>(
    useMemo(
      () => ({
        variables: {
          calendarIds:
            data.connectedCalendars?.map((calendar) => calendar.id) ?? [],
          connections: connection ? [connection] : [],
        },
        subscription: graphql`
          subscription WidgetCalendarOverviewEventCreatedSubscription(
            $calendarIds: [ID!]!
            $connections: [ID!]!
          ) {
            calendarEventCreated(calendarIds: $calendarIds) {
              calendarEvent
                @prependNode(
                  connections: $connections
                  edgeTypeName: "CalendarTeamCalendarEventsEdge"
                ) {
                id
                startAt {
                  isoString
                }
                endAt {
                  isoString
                }
                createdAt {
                  isoString
                }
                isAllDayEvent
                ...WidgetCalendarOverviewEventItem_event
              }
            }
          }
        `,
      }),
      [data.connectedCalendars, connection]
    ),
    { skip: !!props.skipSubscriptions }
  );

  useSubscriptionWithSkip<WidgetCalendarOverviewEventDeletedSubscription>(
    useMemo(
      () => ({
        variables: {
          calendarIds:
            data.connectedCalendars?.map((calendar) => calendar.id) ?? [],
          connections: connection ? [connection] : [],
        },
        subscription: graphql`
          subscription WidgetCalendarOverviewEventDeletedSubscription(
            $calendarIds: [ID!]!
            $connections: [ID!]!
          ) {
            calendarEventDeleted(calendarIds: $calendarIds) {
              calendarEvent {
                id @deleteEdge(connections: $connections)
              }
            }
          }
        `,
      }),
      [connection, data.connectedCalendars]
    ),
    { skip: !!props.skipSubscriptions }
  );

  useSubscriptionWithSkip<WidgetCalendarOverviewEventUpdatedSubscription>(
    useMemo(
      () => ({
        variables: {
          calendarIds:
            data.connectedCalendars?.map((calendar) => calendar.id) ?? [],
        },
        subscription: graphql`
          subscription WidgetCalendarOverviewEventUpdatedSubscription(
            $calendarIds: [ID!]!
          ) {
            calendarEventUpdated(calendarIds: $calendarIds) {
              calendarEvent {
                id
                startAt {
                  isoString
                }
                endAt {
                  isoString
                }
                createdAt {
                  isoString
                }
                isAllDayEvent
                ...WidgetCalendarOverviewEventItem_event
              }
            }
          }
        `,
      }),
      [data.connectedCalendars]
    ),
    { skip: !!props.skipSubscriptions }
  );

  const listData = useMemo(() => {
    return createCalendarOverviewListData(data.calendarEvents?.edges ?? []);
  }, [data.calendarEvents?.edges]);

  const onPressEvent = props.onPressEvent;

  const renderItem = useCallback(
    ({ item, index }: { item: (typeof listData)[number]; index: number }) => {
      if (item.itemType === "week") {
        return (
          <WidgetCalendarOverviewWeekItem
            isFirst={index === 0}
            date={item.date}
            numberOfEvents={item.numberOfEvents}
          />
        );
      }

      return (
        <TouchableBox
          spaceVertical="xsmall"
          spaceHorizontal="small"
          disabled={!onPressEvent}
          onPress={() => {
            onPressEvent?.(
              item.id,
              data.calendarEvents?.__id ? [data.calendarEvents.__id] : []
            );
          }}
        >
          <WidgetCalendarOverviewEventItem
            event={item}
            isActive={item.isActive}
          />
        </TouchableBox>
      );
    },
    [onPressEvent, data.calendarEvents?.__id]
  );

  const ListFooterComponent = useMemo(
    () => (
      <Box style={{ flex: 1 }} spaceTop="xxlarge">
        {isLoadingNext ? <ActivityIndicator /> : null}
      </Box>
    ),
    [isLoadingNext]
  );

  const onEndReached = useCallback(() => {
    if (hasNext && !isLoadingNext && props.scrollEnabled) {
      loadNext(20);
    }
  }, [hasNext, isLoadingNext, props.scrollEnabled, loadNext]);

  const keyExtractor = useCallback((item: (typeof listData)[number]) => {
    if (item.itemType === "week") {
      return `week-${item.date.toISO()}`;
    }

    return item.id;
  }, []);

  const contentContainerStyle = useBoxStyle({
    spaceHorizontal: "none",
  });

  return (
    <>
      <FlatList
        data={listData}
        renderItem={renderItem}
        keyExtractor={keyExtractor}
        ListFooterComponent={ListFooterComponent}
        showsVerticalScrollIndicator={Platform.OS !== "web"}
        contentContainerStyle={contentContainerStyle}
        scrollEnabled={props.scrollEnabled}
        onEndReached={props.scrollEnabled ? onEndReached : undefined}
        onEndReachedThreshold={props.scrollEnabled ? 0.5 : undefined}
      />
      {props.onPressAdd ? (
        <WidgetAddItemButton
          onPress={() =>
            props.onPressAdd?.(
              data.calendarEvents?.__id ? [data.calendarEvents.__id] : []
            )
          }
        />
      ) : null}
    </>
  );
}

export function WidgetCalendarOverview(
  props: {
    widget: WidgetCalendarOverview_widget$key;
    fetchFirst?: number;
  } & BaseWidgetProps
) {
  const widget = useFragment(
    graphql`
      fragment WidgetCalendarOverview_widget on WidgetCalendarOverview {
        id
        title
      }
    `,
    props.widget
  );

  const [fetchKey, setFetchKey] = useState(0);
  useSubscribeToInvalidationState([widget.id], () => {
    setFetchKey((fk) => fk + 1);
  });
  const query = useLazyLoadQuery<WidgetCalendarOverviewQuery>(
    graphql`
      query WidgetCalendarOverviewQuery(
        $widgetId: ID!
        $first: Int
        $after: String
      ) {
        widgetCalendarOverview(id: $widgetId) {
          ...WidgetCalendarOverviewContent_widget
          calendars(includeExternal: false) {
            id
          }
        }
      }
    `,
    {
      widgetId: widget.id,
      first: props.fetchFirst,
    },
    {
      fetchKey: fetchKey + (props.fetchKey ?? ""),
      fetchPolicy: "store-and-network",
    }
  );

  const setFocus = useSetWidgetFocus(widget.id, props.storecastId);
  const onPress = useCallback(() => {
    if (props.mode === "FOCUS" && !props.isFocused) {
      setFocus();
    }
  }, [props.isFocused, props.mode, setFocus]);

  const onEvent = props.onEvent;
  const onPressEvent = useCallback(
    (eventId: string, connections: string[]) => {
      onEvent?.({
        kind: "CalendarEvent",
        eventId,
        widgetId: widget.id,
        connections,
      });
    },
    [onEvent, widget.id]
  );

  const onPressAdd = useCallback(
    (connections: string[]) => {
      onEvent?.({
        kind: "CalendarEventCreate",
        widgetId: widget.id,
        connections,
      });
    },
    [onEvent, widget.id]
  );

  if (!query.widgetCalendarOverview) {
    return null;
  }

  return (
    <TouchableBox
      disabled={props.mode !== "FOCUS"}
      onPress={() => onPress()}
      style={{
        flex: 1,
      }}
      color="surface-primary"
    >
      <AHWidgetTitle>{widget.title}</AHWidgetTitle>
      <WidgetCalendarOverviewContent
        widget={query.widgetCalendarOverview}
        scrollEnabled={
          props.idiom === "TOUCH_READONLY" || props.idiom === "TOUCH"
        }
        onPressEvent={
          props.idiom === "TOUCH" && props.mode !== "FOCUS"
            ? onPressEvent
            : undefined
        }
        onPressAdd={
          props.idiom === "TOUCH" &&
          query.widgetCalendarOverview.calendars.length > 0
            ? onPressAdd
            : undefined
        }
      />
    </TouchableBox>
  );
}

export function WidgetCalendarOverviewPreview(props: {
  widget: WidgetCalendarOverviewPreview_widget$key;
}) {
  const widget = useFragment(
    graphql`
      fragment WidgetCalendarOverviewPreview_widget on WidgetCalendarOverview {
        id
        title
        ...WidgetCalendarOverviewContent_widget
      }
    `,
    props.widget
  );

  return (
    <Box
      style={{
        flex: 1,
      }}
      color="surface-primary"
    >
      <AHWidgetTitle>{widget.title}</AHWidgetTitle>
      <WidgetCalendarOverviewContent
        widget={widget}
        scrollEnabled={false}
        skipSubscriptions
      />
    </Box>
  );
}
